import React from 'react'
import { useAccount } from 'wagmi'
import { usePortfolioData } from './usePortfolioData'
import { useUserReserves } from './useUserReserves'
import { AAVE_V3_BASE_TOKENS } from '../data/tokens'

export interface UserPosition {
  tokenAddress: string
  symbol: string
  suppliedAmount: string
  borrowedAmount: string
  availableToBorrow: string
  availableToWithdraw: string
  availableToRepay: string
}

// Hook to get user positions using portfolio data and individual user reserves
export function useUserPositions(aaveData?: any[], enableDetailedReserves = false) {
  const { address } = useAccount()
  const { data: portfolioData, isLoading: portfolioLoading, error: portfolioError } = usePortfolioData(aaveData)
  const { data: userReserves, isLoading: reservesLoading, error: reservesError } = useUserReserves(aaveData, enableDetailedReserves)

  // Calculate positions based on portfolio data and individual user reserves
  const positions: UserPosition[] = React.useMemo(() => {
    console.log('[positions] Portfolio data:', portfolioData)
    console.log('[positions] User reserves:', userReserves)
    
    // If we have user reserves data, use it for accurate individual token positions
    if (userReserves && userReserves.length > 0) {
      console.log('[positions] Using user reserves data for accurate positions')
      
      return AAVE_V3_BASE_TOKENS.map(token => {
        // Find the user reserve data for this token
        const userReserve = userReserves.find(reserve => 
          reserve.tokenAddress.toLowerCase() === token.address.toLowerCase()
        )
        
        if (userReserve) {
          console.log(`[positions] ${token.symbol}:`)
          console.log(`[positions]   Supplied: ${userReserve.suppliedAmount}`)
          console.log(`[positions]   Borrowed: ${userReserve.borrowedAmount}`)
          console.log(`[positions]   Available to withdraw: ${userReserve.availableToWithdraw}`)
          console.log(`[positions]   Available to repay: ${userReserve.availableToRepay}`)
          
          // Calculate available to borrow based on supplied amount and LTV
          let availableToBorrow = "0"
          if (parseFloat(userReserve.suppliedAmount) > 0 && aaveData && aaveData.length > 0) {
            const reserveData = aaveData.find(reserve => 
              reserve.underlyingAsset?.toLowerCase() === token.address.toLowerCase()
            )
            
            if (reserveData && reserveData.ltv && reserveData.priceInUsd) {
              const suppliedUSD = parseFloat(userReserve.suppliedAmount) * parseFloat(reserveData.priceInUsd)
              const tokenLTV = reserveData.ltv / 100 // LTV is already in percentage form
              const borrowPowerUSD = suppliedUSD * tokenLTV
              const tokenPriceUSD = parseFloat(reserveData.priceInUsd)
              
              if (tokenPriceUSD > 0) {
                const tokenAmount = borrowPowerUSD / tokenPriceUSD
                availableToBorrow = tokenAmount.toFixed(8)
                console.log(`[positions]   Available to borrow: ${availableToBorrow} ($${borrowPowerUSD.toFixed(2)})`)
              }
            }
          }
          
          return {
            tokenAddress: token.address,
            symbol: token.symbol,
            suppliedAmount: userReserve.suppliedAmount,
            borrowedAmount: userReserve.borrowedAmount,
            availableToBorrow,
            availableToWithdraw: userReserve.availableToWithdraw,
            availableToRepay: userReserve.availableToRepay
          }
        }
        
        // No user reserve data for this token
        return {
          tokenAddress: token.address,
          symbol: token.symbol,
          suppliedAmount: "0",
          borrowedAmount: "0",
          availableToBorrow: "0",
          availableToWithdraw: "0",
          availableToRepay: "0"
        }
      })
    }
    
    // Fallback to portfolio data if user reserves are not available
    if (portfolioData && portfolioData.totalSupplied && parseFloat(portfolioData.totalSupplied) > 0) {
      console.log('[positions] Using portfolio data for positions (fallback)')
      const totalSuppliedUSD = parseFloat(portfolioData.totalSupplied) || 0
      const totalBorrowedUSD = parseFloat(portfolioData.totalBorrowed) || 0
      const availableBorrowsUSD = portfolioData.availableBorrows || 0
      
      return AAVE_V3_BASE_TOKENS.map(token => {
        // Calculate per-token borrowing power based on individual LTV ratios
        let tokenBorrowPowerUSD = 0
        
        if (aaveData && aaveData.length > 0) {
          const reserveData = aaveData.find(reserve => 
            reserve.underlyingAsset?.toLowerCase() === token.address.toLowerCase()
          )
          
          if (reserveData && reserveData.ltv) {
            const tokenLTV = reserveData.ltv / 100
            tokenBorrowPowerUSD = totalSuppliedUSD * tokenLTV
          } else {
            const isStablecoin = ['USDC', 'USDbC', 'EURC', 'GHO'].includes(token.symbol)
            const estimatedLTV = isStablecoin ? 0.85 : 0.75
            tokenBorrowPowerUSD = totalSuppliedUSD * estimatedLTV
          }
        } else {
          tokenBorrowPowerUSD = availableBorrowsUSD
        }
        
        // Convert USD borrowing power to token amount
        let availableToBorrowTokenAmount = "0"
        if (tokenBorrowPowerUSD > 0 && aaveData && aaveData.length > 0) {
          const reserveData = aaveData.find(reserve => 
            reserve.underlyingAsset?.toLowerCase() === token.address.toLowerCase()
          )
          
          if (reserveData && reserveData.priceInUsd) {
            const tokenPriceUSD = parseFloat(reserveData.priceInUsd)
            if (tokenPriceUSD > 0) {
              const tokenAmount = tokenBorrowPowerUSD / tokenPriceUSD
              availableToBorrowTokenAmount = tokenAmount.toFixed(8)
            }
          }
        }
        
        return {
          tokenAddress: token.address,
          symbol: token.symbol,
          suppliedAmount: "0", // Cannot determine individual amounts without user reserves
          borrowedAmount: "0", // Cannot determine individual amounts without user reserves
          availableToBorrow: availableToBorrowTokenAmount,
          availableToWithdraw: "0", // Cannot determine individual amounts without user reserves
          availableToRepay: "0" // Cannot determine individual amounts without user reserves
        }
      })
    }
    
    // No data available, return empty positions
    console.log('[positions] No data available, returning empty positions')
    return AAVE_V3_BASE_TOKENS.map(token => ({
      tokenAddress: token.address,
      symbol: token.symbol,
      suppliedAmount: "0",
      borrowedAmount: "0",
      availableToBorrow: "0",
      availableToWithdraw: "0",
      availableToRepay: "0"
    }))
  }, [portfolioData, userReserves, aaveData])

  return {
    data: positions,
    isLoading: portfolioLoading || reservesLoading,
    error: portfolioError || reservesError
  }
}

// Helper to get position for a specific token
export function useTokenPosition(tokenAddress: string, aaveData?: any[], enableDetailedReserves = false) {
  const { data: positions, isLoading, error } = useUserPositions(aaveData, enableDetailedReserves)

  const position = positions?.find(p => p.tokenAddress.toLowerCase() === tokenAddress.toLowerCase())

  return {
    position,
    isLoading,
    error
  }
}